import { ButtonBase } from '@mui/material'
import { styled } from '../../utils/styled'
import type { Theme } from '../theme'
import Typography from '../Typography'
import Grid from '../Grid'

export const StyledMessage = styled('div')(({ theme }) => ({
  color: theme.tokens.foreground.main.primary,
  display: 'flex',
  alignItems: 'center',
  fontFamily: theme.typography.body3.fontFamily,
  fontSize: theme.typography.body3.fontSize,
  marginTop: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  span: {
    paddingLeft: theme.spacing(1)
  }
}))

const getCalendarColors = (
  theme: Theme
): {
  surface: string
  text: string
  muted: string
  brand: string
  onBrand: string
  range: string
  comparison: string
  comparisonStrong: string
  overlap: string
  overlapDark: string
  border: string
  hover: string
  soft: string
  error: string
  radius: string
} => ({
  surface: theme.tokens.background.main.default,
  text: theme.tokens.foreground.main.primary,
  muted: theme.tokens.foreground.main.secondary,
  brand: theme.tokens.background.brand.main.default,
  onBrand: theme.tokens.foreground.main.staticWhite,
  range: theme.tokens.background.brand.tonal.soft.default,
  comparison: theme.tokens.dataViz.primary[3].fill.subtle,
  comparisonStrong: theme.tokens.dataViz.primary[3].fill.default,
  overlap: theme.tokens.dataViz.primary[5].fill.default,
  overlapDark: theme.tokens.dataViz.primary[5].border.default,
  border: theme.tokens.border.main.tonal,
  hover: theme.tokens.background.tonal.superSoft.hover,
  soft: theme.tokens.background.tonal.soft.default,
  error: theme.tokens.foreground.semantic.error.default,
  radius: theme.borderRadius.medium
})

export const StyledDatePickerContainer = styled(Grid)<{ $isComparisonMode?: boolean }>(({ theme }) => {
  const c = getCalendarColors(theme)
  return {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: c.surface,
    borderRadius: c.radius,
    overflow: 'hidden',
    height: 'auto'
  }
})

export const StyledCalendarGrid = styled(Grid)`
  display: grid;
  grid-template-columns: repeat(7, 40px);
  column-gap: 0;
  row-gap: 2px;
  padding: 0 16px;
  justify-content: center;
`

interface TileBorderProps {
  $isOverlapStart?: boolean
  $isOverlapEnd?: boolean
  $isRangeEnd?: boolean
  $isRangeStart?: boolean
  $isComparisonRangeEnd?: boolean
  $isComparisonRangeStart?: boolean
  $isInRange?: boolean
  $isInComparisonRange?: boolean
  $isFirstInWeek?: boolean
  $isLastInWeek?: boolean
  $isFirstInMonth?: boolean
  $isLastInMonth?: boolean
}

const tileRadius = (radius: string, corners: string): string => `border-radius: ${corners.replace(/16px/g, radius)};`

const getOverlapEndRadius = (radius: string, isRangeEnd?: boolean): string =>
  isRangeEnd === true ? tileRadius(radius, '16px') : tileRadius(radius, '0 16px 16px 0')

const getRangeEndRadius = (radius: string, isFirstInWeek?: boolean, isFirstInMonth?: boolean): string =>
  isFirstInWeek === true || isFirstInMonth === true ? '' : tileRadius(radius, '0 16px 16px 0')

const getRangeStartRadius = (radius: string, isLastInWeek?: boolean, isLastInMonth?: boolean): string =>
  isLastInWeek === true || isLastInMonth === true ? '' : tileRadius(radius, '16px 0 0 16px')

const getInRangeRadius = (
  radius: string,
  isFirstInWeek?: boolean,
  isLastInWeek?: boolean,
  isFirstInMonth?: boolean,
  isLastInMonth?: boolean
): string => {
  const isFirst = isFirstInWeek === true || isFirstInMonth === true
  const isLast = isLastInWeek === true || isLastInMonth === true
  if (isFirst && isLast) return ''
  if (isFirst) return tileRadius(radius, '16px 0 0 16px')
  if (isLast) return tileRadius(radius, '0 16px 16px 0')
  return tileRadius(radius, '0')
}

const getTileBorderRadius = (radius: string, props: TileBorderProps): string => {
  if (props.$isOverlapStart === true && props.$isOverlapEnd !== true) return tileRadius(radius, '0')
  if (props.$isOverlapEnd === true && props.$isOverlapStart !== true && props.$isRangeEnd === true)
    return tileRadius(radius, '0')
  if (props.$isOverlapEnd === true && props.$isOverlapStart !== true)
    return getOverlapEndRadius(radius, props.$isRangeEnd)
  if (props.$isRangeStart === true && props.$isRangeEnd === true) return ''
  if (props.$isRangeEnd === true || props.$isComparisonRangeEnd === true)
    return getRangeEndRadius(radius, props.$isFirstInWeek, props.$isFirstInMonth)
  if (props.$isRangeStart === true || props.$isComparisonRangeStart === true)
    return getRangeStartRadius(radius, props.$isLastInWeek, props.$isLastInMonth)
  if (props.$isInRange === true || props.$isInComparisonRange === true)
    return getInRangeRadius(
      radius,
      props.$isFirstInWeek,
      props.$isLastInWeek,
      props.$isFirstInMonth,
      props.$isLastInMonth
    )
  return ''
}

type DateTileProps = TileBorderProps & {
  $isSelected?: boolean
  $isRangeEndInComparisonRange?: boolean
  $isComparisonDate?: boolean
  $isOverlap?: boolean
  $isToday?: boolean
  $isDisabled?: boolean
  $isOutsideMonth?: boolean
}

export const StyledDateTile = styled(ButtonBase)<DateTileProps>(({ theme, ...props }) => {
  const c = getCalendarColors(theme)
  const r = c.radius

  return `
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 12px 16px;
  border: none;
  border-radius: ${r};
  background-color: transparent;
  color: ${c.text};
  font-family: ${theme.typography.h5.fontFamily};
  font-size: ${theme.typography.h5.fontSize};
  font-weight: 500;
  line-height: 16px;
  cursor: pointer;
  transition: background-color 80ms ease-out, color 80ms ease-out;

  ${props.$isOutsideMonth === true ? `color: ${c.muted};` : ''}

  ${
    props.$isSelected === true && props.$isInRange !== true
      ? `
    background-color: ${c.brand};
    color: ${c.onBrand};
  `
      : ''
  }

  ${
    props.$isInRange === true
      ? `
    background-color: ${c.range};
    color: ${c.text};
  `
      : ''
  }

  ${
    props.$isRangeStart === true && props.$isRangeEnd !== true && props.$isLastInWeek !== true
      ? `
    background-color: ${c.range};
    color: ${c.onBrand};

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background-color: ${c.brand};
      border-radius: ${r};
      z-index: -1;
    }
  `
      : ''
  }

  ${
    props.$isRangeStart === true && props.$isRangeEnd !== true && props.$isLastInWeek === true
      ? `
    background-color: ${c.brand};
    color: ${c.onBrand};
  `
      : ''
  }

  ${
    props.$isRangeEnd === true && props.$isRangeStart !== true && props.$isFirstInWeek !== true
      ? `
    background-color: ${c.range};
    color: ${c.onBrand};

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background-color: ${c.brand};
      border-radius: ${r};
      z-index: -1;
    }
  `
      : ''
  }

  ${
    props.$isRangeEnd === true && props.$isRangeStart !== true && props.$isFirstInWeek === true
      ? `
    background-color: ${c.brand};
    color: ${c.onBrand};
  `
      : ''
  }

  ${
    props.$isRangeStart === true && props.$isRangeEnd === true
      ? `
    background-color: ${c.brand};
    color: ${c.onBrand};
  `
      : ''
  }

  ${
    props.$isComparisonDate === true && props.$isInComparisonRange !== true
      ? `
    background-color: ${c.comparison};
    color: ${c.text};
  `
      : ''
  }

  ${
    props.$isInComparisonRange === true
      ? `
    background-color: ${c.comparison};
    color: ${c.text};
  `
      : ''
  }

  ${
    props.$isComparisonRangeStart === true || props.$isComparisonRangeEnd === true
      ? `
    background-color: ${c.comparison};
    color: ${c.text};
  `
      : ''
  }

  ${getTileBorderRadius(r, props)}

  ${
    props.$isOverlap === true && props.$isOverlapStart !== true && props.$isOverlapEnd !== true
      ? `
    background-color: ${c.overlap};
    color: ${c.onBrand};
  `
      : ''
  }

  ${
    props.$isOverlapStart === true && props.$isOverlapEnd !== true
      ? `
    background: linear-gradient(to left, ${c.overlap} 16.25%, ${c.range} 75%);
    color: ${c.onBrand};
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background-color: ${c.overlap};
      border-radius: ${r};
      z-index: -1;
    }
  `
      : ''
  }

  ${
    props.$isOverlapEnd === true && props.$isOverlapStart !== true && props.$isRangeEnd !== true
      ? `
    background-color: ${c.overlap};
    color: ${c.onBrand};
  `
      : ''
  }

  ${
    props.$isOverlapStart === true && props.$isOverlapEnd === true
      ? `
    background-color: ${c.overlap};
    color: ${c.onBrand};
  `
      : ''
  }

  ${
    props.$isRangeEndInComparisonRange === true
      ? `
    && {
      background: linear-gradient(to right, ${c.overlap} 50%, ${c.comparison} 50%);
      color: ${c.onBrand};
    }

    &&::before {
      content: '';
      position: absolute;
      inset: 0;
      background-color: ${c.overlapDark};
      border-radius: ${r};
      z-index: -1;
    }
  `
      : ''
  }

  ${
    props.$isDisabled === true
      ? `
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  `
      : ''
  }

  &:hover:not(:disabled) {
    background-color: ${c.hover};
  }

  ${
    props.$isInRange === true ||
    (props.$isRangeStart === true && props.$isRangeEnd !== true && props.$isLastInWeek !== true) ||
    (props.$isRangeEnd === true && props.$isRangeStart !== true && props.$isFirstInWeek !== true)
      ? `
    &:hover:not(:disabled) {
      background-color: ${c.range};
    }
  `
      : ''
  }

  ${
    props.$isRangeStart === true && props.$isRangeEnd === true
      ? `
    &:hover:not(:disabled) {
      background-color: ${c.brand};
      color: ${c.onBrand};
    }
  `
      : ''
  }

  ${
    ((props.$isSelected === true &&
      props.$isInRange !== true &&
      props.$isRangeStart !== true &&
      props.$isInComparisonRange !== true &&
      props.$isComparisonRangeStart !== true &&
      props.$isComparisonRangeEnd !== true) ||
      (props.$isRangeEnd === true && props.$isFirstInWeek === true) ||
      (props.$isRangeStart === true && props.$isRangeEnd !== true && props.$isLastInWeek === true)) &&
    props.$isDisabled !== true
      ? `
    &:hover:not(:disabled) {
      background-color: ${c.brand};
      color: ${c.onBrand};
    }
  `
      : ''
  }

  ${
    props.$isInComparisonRange === true ||
    props.$isComparisonRangeStart === true ||
    props.$isComparisonRangeEnd === true
      ? `
    &:hover:not(:disabled) {
      background-color: ${c.comparison};
      color: ${c.text};
    }
  `
      : ''
  }

  ${
    props.$isOverlap === true ||
    props.$isOverlapStart === true ||
    props.$isOverlapEnd === true ||
    props.$isRangeEndInComparisonRange === true
      ? `
    &:hover:not(:disabled) {
      background-color: ${c.overlap};
      color: ${c.onBrand};
    }
  `
      : ''
  }
`
})

export const StyledCalendarHeader = styled(Grid)(({ theme }) => {
  const c = getCalendarColors(theme)
  return `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${c.border};
  }

  &::before {
    margin-right: 16px;
  }

  &::after {
    margin-left: 16px;
  }
`
})

export const StyledMonthYear = styled(Typography)(({ theme }) => ({
  color: theme.tokens.foreground.main.primary,
  whiteSpace: 'nowrap'
}))

export const StyledSidebar = styled(Grid)(({ theme }) => {
  const c = getCalendarColors(theme)
  return {
    position: 'relative',
    width: '220px',
    flexShrink: 0,
    minHeight: 0,
    backgroundColor: c.surface,
    borderRight: `1px solid ${c.border}`
  }
})

export const StyledSidebarContent = styled(Grid)`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`

export const StyledPresetsContainer = styled('ul')(({ theme }) => {
  const c = getCalendarColors(theme)
  return `
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: ${c.brand} transparent;

  .MuiTypography-body1 {
    font-size: ${theme.typography.body2.fontSize};
    color: ${c.text};
  }
`
})

export const StyledComparisonCard = styled(Grid)(({ theme }) => {
  const c = getCalendarColors(theme)
  return {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: c.soft,
    border: `1px solid ${c.soft}`,
    borderRadius: c.radius,
    padding: '4px 8px'
  }
})

export const StyledComparisonHeader = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 4px;
`

export const StyledComparisonHeaderLabel = styled(Typography)(({ theme }) => ({
  color: theme.tokens.foreground.main.primary,
  fontWeight: 500
}))

export const StyledWeekDay = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.tokens.foreground.main.secondary,
  letterSpacing: '0.5px'
}))

export const StyledFooter = styled(Grid)(({ theme }) => {
  const c = getCalendarColors(theme)
  return {
    display: 'flex',
    gap: '8px',
    padding: '16px 0',
    borderTop: `1px solid ${c.border}`,
    justifyContent: 'flex-end'
  }
})

export const StyledDatePickerContent = styled(Grid)`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  flex: 1;
  min-width: 0;
`

export const StyledDatePickerBody = styled(Grid)`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex: 1;
  min-height: 0;
`

export const StyledCalendarContentWrapper = styled(Grid)<{ $contentMaxHeight?: number }>(
  ({ theme, $contentMaxHeight: contentMaxHeight }) => {
    const c = getCalendarColors(theme)
    return {
      flex: 1,
      minHeight: 0,
      maxHeight: contentMaxHeight == null ? '500px' : `${contentMaxHeight}px`,
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarGutter: 'stable',
      scrollbarWidth: 'thin',
      scrollbarColor: `${c.brand} transparent`
    }
  }
)

export const StyledSelectedDateHeader = styled(Grid)(({ theme }) => {
  const c = getCalendarColors(theme)
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    margin: '20px 20px 0 16px',
    borderBottom: `1px solid ${c.border}`
  }
})

export const StyledHeaderShadow = styled(Grid)`
  position: absolute;
  bottom: -18px;
  left: 0;
  right: 0;
  height: 18px;
  background: linear-gradient(to bottom, rgba(112, 144, 176, 0.12), rgba(255, 255, 255, 0));
  pointer-events: none;
  z-index: 1;
`

export const StyledSelectedDateRow = styled(Grid)`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const StyledSelectedDateDot = styled(Grid)<{ $variant?: 'primary' | 'comparison' }>(
  ({ theme, $variant: variant }) => {
    const c = getCalendarColors(theme)
    return {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: variant === 'comparison' ? c.comparisonStrong : c.brand,
      flexShrink: 0
    }
  }
)

export const StyledSelectedDateLabel = styled(Typography)(({ theme }) => ({
  color: theme.tokens.foreground.main.secondary
}))

export const StyledCopyButton = styled(ButtonBase)(({ theme }) => {
  const c = getCalendarColors(theme)
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '2px',
    color: c.muted,
    transition: 'color 200ms ease-in-out',
    '&:hover': {
      color: theme.tokens.foreground.main.secondary
    },
    '& svg': {
      width: '24px',
      height: '24px'
    }
  }
})

export const StyledDateInputWrapper = styled(Grid)<{ $hasError?: boolean }>`
  align-self: flex-start;
  margin-bottom: ${(props) => (props.$hasError === true ? '4px' : '16px')};
  width: 140px;

  .MuiOutlinedInput-root {
    padding-right: 0;
  }
`

export const StyledDateRangeInputs = styled(Grid)<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: ${(props) => (props.$hasError === true ? '4px' : '12px')};
`

export const StyledDateRangeInputItem = styled(Grid)`
  width: 140px;

  .MuiOutlinedInput-root {
    padding-right: 0;
  }
`

export const StyledDateRangeSeparator = styled(Typography)(({ theme }) => ({
  color: theme.tokens.foreground.main.secondary,
  userSelect: 'none'
}))

export const StyledHeaderRows = styled(Grid)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const StyledSelectedDateWeekDays = styled(Grid)`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
`

export const StyledSelectedDateWeekDay = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  padding: 2px 0;
`

export const StyledMonthContainer = styled(Grid)`
  &:last-child {
    border-bottom: none;
  }
`

export const StyledErrorRow = styled(Grid)`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
`

export const StyledErrorDot = styled(Grid)(({ theme }) => {
  const c = getCalendarColors(theme)
  return {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: c.error,
    flexShrink: 0
  }
})

export const StyledErrorText = styled(Typography)(({ theme }) => ({
  color: theme.tokens.foreground.semantic.error.default
}))
